import React from 'react';
import styled from 'styled-components';

import { Banner, Type } from '@spotify-internal/encore-creator-web';
import { gray50, spacer20 } from '@spotify-internal/encore-foundation';

import { FundraisingBannerWrapper } from '../../FundraisingBanner';
import { unreachable } from '../../lib/unreachable';
import { WizardStep } from '../../lib/useWizard';
import { DialogWrapper } from '../DialogWrapper';
import { useArtistFundraisingPickSelfServeRollout } from '../../../utils';

type FormTitleProps = { isEditing?: boolean; step: WizardStep };

export function FormTitle({ isEditing, step }: FormTitleProps) {
  const selfServeRolledOut = useArtistFundraisingPickSelfServeRollout();

  let confirmationSubheading =
    'Your fans will be directed to this link on your artist profile.';

  if (!selfServeRolledOut) {
    confirmationSubheading +=
      " You won't be able to edit this after you submit it";
  }

  switch (step) {
    case WizardStep.input: {
      let inputHeadingText =
        'Add a fundraising link so your fans can support you or your cause directly';
      if (isEditing) {
        inputHeadingText = 'Edit your fundraising link';
      }
      return (
        <DialogWrapper style={{ flexDirection: 'column' }}>
          <H2 style={{ maxWidth: '772px' }}>{inputHeadingText}</H2>
        </DialogWrapper>
      );
    }
    case WizardStep.failure:
      return (
        <>
          <GenericSubmissionErrorBanner />
          <DialogBodyTitle
            heading2="Confirm your Artist Fundraising Pick is correct"
            subheading2={confirmationSubheading}
          />
        </>
      );
    case WizardStep.submit:
      return (
        <DialogBodyTitle
          heading2="Confirm your Artist Fundraising Pick is correct"
          subheading2={confirmationSubheading}
        />
      );
    case WizardStep.submitting:
      return null;
    case WizardStep.success:
      return null;

    case WizardStep.conflict:
      // TODO cleanup once self-serve is rolled out and this response code is deprecated.
      return (
        <DialogBodyTitle
          heading2="Your fundraising link was not submitted"
          subheading2="You can’t submit another link because you, or someone from your team, already submitted an Artist Fundraising Pick. Contact other admins on your team for more info."
        />
      );

    default:
      unreachable(step);
      return null;
  }
}

const H2 = styled(Type).attrs({ forwardedAs: 'h2', variant: Type.heading2 })`
  padding-bottom: ${spacer20};
  text-align: start;
`;

const H3 = styled(Type).attrs({
  forwardedAs: 'h3',
  variant: Type.body1,
  color: gray50,
})`
  text-align: start;
`;

type Props = { heading2: string; subheading2?: string };

function DialogBodyTitle(props: Props) {
  return (
    <DialogWrapper style={{ flexDirection: 'column' }}>
      <H2>{props.heading2}</H2>
      {props.subheading2 ? <H3>{props.subheading2}</H3> : null}
    </DialogWrapper>
  );
}

const FundraisingFormBanner = styled(Banner)`
  margin-bottom: 40px;
  text-align: start;
`;

function GenericSubmissionErrorBanner() {
  const [open, setOpen] = React.useState(true);
  return open ? (
    <FundraisingBannerWrapper>
      <FundraisingFormBanner
        variant={Banner.error}
        onClose={() => {
          setOpen(false);
        }}
      >
        Something went wrong. Please try again.
      </FundraisingFormBanner>
    </FundraisingBannerWrapper>
  ) : null;
}
